import React, { useEffect, useState } from "react";
import SimulationApi from "../../api/SimulationApi";
import { EModuleType } from "../../Model/EModuleType";
import { SimulatedScannedBarcode } from "../../Model/Simulation";
import { StorageConfig } from "../../Model/StorageConfig";
import { getModulesByType } from "../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getStorageConfig } from "../../redux/reducers/StorageConfigReducer";
import MtiButton from "../Buttons/MtiButton";
import TextBox from "../Common/TextBox";
import "./SimulatedScannedBarcodeEditor.css";

export interface ScannedSlotData {
  id: string;
  display: string;
  selected: boolean;
}

function makeRandomBarcode(length: number) {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

function getSlots(
  moduleIdentifier: string,
  storageConfig: StorageConfig,
  simulatedScannedBarcode: SimulatedScannedBarcode[]
) {
  let index = 0;
  let result: SimulatedScannedBarcode[] = [];
  storageConfig.hotels.forEach((hotel) => {
    const hotelNumber = hotel.number;
    const capacity = hotel.capacity;
    for (let slotIndex = 0; slotIndex < capacity; slotIndex++) {
      const barcode =
        simulatedScannedBarcode.filter(
          (s) =>
            s.identifier === moduleIdentifier &&
            s.hotelNumber === hotelNumber &&
            s.slotNumber === slotIndex + 1
        )[0]?.barcode ?? "";
      result.push({
        identifier: moduleIdentifier,
        hotelNumber: hotelNumber,
        slotNumber: slotIndex + 1,
        barcode: barcode,
      });
      index++;
    }
  });
  return result;
}

type Props = { moduleType: EModuleType };

export default function SimulatedScannedBarcodeEditor({ moduleType }: Props) {
  const storageModule = useTypedSelector(
    (state) => getModulesByType(state)[moduleType][0]
  );
  const storageConfig = useTypedSelector((state) =>
    getStorageConfig(state, storageModule.identifier)
  );
  const [simulatedScannedBarcode, setSimulatedScannedBarcode] = useState<
    SimulatedScannedBarcode[]
  >([]);

  useEffect(() => {
    SimulationApi.getSimulatedScannedBarcode().then(
      (simulatedScannedBarcode) => {
        setSimulatedScannedBarcode(simulatedScannedBarcode);
      }
    );
  }, [storageConfig, storageModule.identifier]);

  const handleOnChange = (index: number, barcode: string) => {
    const anotherIdentifierSlots = simulatedScannedBarcode.filter(
      (simulate) => simulate.identifier !== storageModule.identifier
    );
    const currIdentifierSlots = getSlots(
      storageModule.identifier,
      storageConfig,
      simulatedScannedBarcode
    );
    currIdentifierSlots[index] = {
      ...currIdentifierSlots[index],
      barcode: barcode,
    };
    setSimulatedScannedBarcode([
      ...currIdentifierSlots,
      ...anotherIdentifierSlots,
    ]);
  };

  const handleSaveSlots = () => {
    SimulationApi.setSimulatedScannedBarcode(simulatedScannedBarcode);
  };

  const generateRandomBarcode = () => {
    const anotherIdentifierSlots = simulatedScannedBarcode.filter(
      (simulate) => simulate.identifier !== storageModule.identifier
    );
    const currIdentifierSlots = getSlots(
      storageModule.identifier,
      storageConfig,
      simulatedScannedBarcode
    );
    const randomBarcodeSlots = currIdentifierSlots.map((storage) => ({
      ...storage,
      barcode: makeRandomBarcode(4),
    }));
    setSimulatedScannedBarcode([
      ...randomBarcodeSlots,
      ...anotherIdentifierSlots,
    ]);
  };

  const handleClearAllBarcode = () => {
    const anotherIdentifierSlots = simulatedScannedBarcode.filter(
      (simulate) => simulate.identifier !== storageModule.identifier
    );
    const currIdentifierSlots = getSlots(
      storageModule.identifier,
      storageConfig,
      simulatedScannedBarcode
    );
    const randomBarcodeSlots = currIdentifierSlots.map((storage) => ({
      ...storage,
      barcode: "",
    }));
    setSimulatedScannedBarcode([
      ...randomBarcodeSlots,
      ...anotherIdentifierSlots,
    ]);
  };

  const simulatedScannedBarcodeByIdentifier = getSlots(
    storageModule.identifier,
    storageConfig,
    simulatedScannedBarcode
  );

  return (
    <div className="simulated-scanned-barcode-editor-container">
      <div className="simulated-scanned-barcode-editor-content">
        {simulatedScannedBarcodeByIdentifier.map((ssb, index) => (
          <div className="simulated-scanned-barcode-editor-line">
            <span className="simulated-scanned-barcode-editor-label-hotel">
              Hotel:{ssb.hotelNumber}, Slot:{ssb.slotNumber}
            </span>
            <TextBox
              className="simulated-scanned-barcode-editor-input"
              name={index.toString()}
              value={ssb.barcode}
              onChange={(value) => handleOnChange(index, value as string)}
            />
          </div>
        ))}
      </div>
      <div className="simulated-scanned-barcode-editor-footer">
        <MtiButton
          variant="tertiary"
          size="large"
          onClick={generateRandomBarcode}
        >
          Generate Random Barcode
        </MtiButton>
        <MtiButton
          variant="secondary"
          colorVariant="black"
          size="large"
          onClick={handleClearAllBarcode}
        >
          Clear
        </MtiButton>
        <MtiButton size="large" onClick={handleSaveSlots}>
          Save
        </MtiButton>
      </div>
    </div>
  );
}
